import React, { useEffect, useState, useRef, useCallback } from "react";
import { useTranslation } from "@helpers/translate";
import { Helmet } from "react-helmet";
import { Quotes } from "@components/pages/Futures/components/Quotes";
import { useDispatch, useSelector } from "react-redux";
import {
  getTickerData,
  subscribeToFutureSockets,
} from "@actions/futures/futureData";
import { Orders } from "@components/pages/Futures/components/Orders";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Controls } from "@components/pages/Futures/components/Controls";
import FuturesChart from "@components/pages/Futures/components/FuturesChart";
import useChartHeightFactor from "./components/FuturesChart/hooks/useChartHeightFactor";
import { FuturesNotification } from "@components/pages/Futures/components/FuturesNotification";
import FuturesOnboarding from "./components/FuturesOnboarding";
import FuturesOnboardingGuide from "./components/FuturesOnboarding/components/FuturesOnboardingGuide";
import { futuresOnboardingSteps } from "./components/FuturesOnboarding/steps";
import {
  BINANCE_STREAM_HOST,
  isBinanceStreamLive,
} from "@helpers/binance/binanceStreams";
import "./Futures.sass";

const FUTURES_ONBOARDING_COMPLETED_KEY = "futures_onboarding_completed";
const TICKER_FALLBACK_POLL_MS = 4000;

export const Futures = () => {
  const { __ } = useTranslation();
  const dispatch = useDispatch();
  const { pair, tickerData } = useSelector((state) => state.futures);
  const { user, isFirstLogin, loading: userLoading } = useSelector(
    (state) => state.user,
  );
  const isTabsLayout = useMediaQuery("(max-width: 1024px)");

  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [closeAllPositions, setCloseAllPositions] = useState({
    run: null,
    disabled: true,
  });
  const handleCloseAllPositionsChange = useCallback((next) => {
    setCloseAllPositions(next);
  }, []);
  const [rightColumnWidth, setRightColumnWidth] = useState(() => {
    if (typeof window === "undefined") return 405;
    const saved = localStorage.getItem("futures_right_column_width");
    return saved ? parseFloat(saved) : 405;
  });
  const [isResizing, setIsResizing] = useState(false);
  const contentRef = useRef(null);
  const chartRef = useRef(null);
  // The resize handle under the chart scales its height; the CSS of both the
  // block and the chart inside it reads --chart-height-factor
  const chartHeight = useChartHeightFactor(chartRef);
  const controlsRef = useRef(null);
  const startXRef = useRef(0);
  const startWidthRef = useRef(405);
  const [isGuideOpen, setIsGuideOpen] = useState(false);

  const onboardingStorageKey = user?.id
    ? `${FUTURES_ONBOARDING_COMPLETED_KEY}_${user.id}`
    : FUTURES_ONBOARDING_COMPLETED_KEY;

  const [isOnboardingCompleted, setIsOnboardingCompleted] = useState(() => {
    if (typeof window === "undefined") return false;

    return (
      localStorage.getItem(FUTURES_ONBOARDING_COMPLETED_KEY) === "true"
    );
  });

  useEffect(() => {
    if (typeof window === "undefined") return;

    setIsOnboardingCompleted(
      localStorage.getItem(onboardingStorageKey) === "true",
    );
  }, [onboardingStorageKey]);

  const shouldShowOnboarding =
    !userLoading && isFirstLogin && !isOnboardingCompleted;

  const completeOnboarding = useCallback(() => {
    setIsOnboardingCompleted(true);

    if (typeof window !== "undefined") {
      localStorage.setItem(onboardingStorageKey, "true");
    }
  }, [onboardingStorageKey]);

  const handleOrderSuccess = () => {
    setRefreshTrigger((prev) => prev + 1);
  };

  const handleDividerMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!contentRef.current || isTabsLayout) return;

    startXRef.current = e.clientX;
    startWidthRef.current = rightColumnWidth || 405;
    setIsResizing(true);
  };

  const handleMouseMove = useCallback(
    (e) => {
      if (!isResizing || !contentRef.current) return;

      const contentRect = contentRef.current.getBoundingClientRect();
      const deltaX = startXRef.current - e.clientX; // перетягування вліво збільшує праву колонку
      const newWidth = startWidthRef.current + deltaX;

      const minWidth = 405; // мінімальна комфортна ширина для панелі
      const maxWidth = Math.max(minWidth, contentRect.width - 1230);

      const clampedWidth = Math.max(minWidth, Math.min(maxWidth, newWidth));
      setRightColumnWidth(clampedWidth);
    },
    [isResizing],
  );

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
    if (typeof window !== "undefined") {
      localStorage.setItem("futures_right_column_width", rightColumnWidth.toString());
    }
  }, [rightColumnWidth]);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMoveGlobal = (e) => {
      e.preventDefault();
      handleMouseMove(e);
    };

    const handleMouseUpGlobal = (e) => {
      e.preventDefault();
      handleMouseUp();
    };

    document.addEventListener("mousemove", handleMouseMoveGlobal, {
      passive: false,
    });
    document.addEventListener("mouseup", handleMouseUpGlobal);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    if (chartRef.current) {
      chartRef.current.style.pointerEvents = "none";
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMoveGlobal);
      document.removeEventListener("mouseup", handleMouseUpGlobal);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";

      if (chartRef.current) {
        chartRef.current.style.pointerEvents = "";
      }
    };
  }, [isResizing, handleMouseMove, handleMouseUp]);

  useEffect(() => {
    let cleanUpWebSocket = () => {};
    let fallbackRefreshInterval = null;
    let isActive = true;

    const fetchAndSubscribe = async () => {
      dispatch(getTickerData());
      const unsubscribe = await dispatch(subscribeToFutureSockets());

      // The effect may be cleaned up while awaiting: do not leave the old pair subscribed
      if (!isActive) {
        unsubscribe();
        return;
      }

      cleanUpWebSocket = unsubscribe;
      fallbackRefreshInterval = setInterval(() => {
        // Ticker and mark price arrive with the stream; poll only while it is down
        if (!isBinanceStreamLive(BINANCE_STREAM_HOST.MARKET)) {
          dispatch(getTickerData());
        }
      }, TICKER_FALLBACK_POLL_MS);
    };

    fetchAndSubscribe();

    return () => {
      isActive = false;
      if (fallbackRefreshInterval) {
        clearInterval(fallbackRefreshInterval);
      }
      cleanUpWebSocket();
    };
  }, [pair.slug]);

  return (
    <div className="futures">
      <Helmet>
        <title>
          {tickerData && pair
            ? `${tickerData.lastPrice} | ${pair.slug}`
            : "Loading..."}
          {__("seo.divider")} {__("seo.title")}
        </title>
      </Helmet>

      <FuturesNotification />

      <div className="futures__content" ref={contentRef}>
        <div className="futures__left-column">
          <Quotes
            onCloseAllPositions={closeAllPositions.run}
            closeAllDisabled={closeAllPositions.disabled}
          />

          <div
            data-futures-guide="trading-chart"
            ref={chartRef}
            className="futures__chart"
            style={{ "--chart-height-factor": chartHeight.factor }}
          >
            <FuturesChart heightHandle={chartHeight} />
          </div>

          <div className="futures__orders">
            <Orders
              refreshTrigger={refreshTrigger}
              isGuideOpen={isGuideOpen}
              onCloseAllPositionsChange={handleCloseAllPositionsChange}
            />
          </div>
        </div>

        <div
          className="futures__divider"
          aria-hidden="true"
          onMouseDown={handleDividerMouseDown}
        />

        <div
          className="futures__right-column"
          style={!isTabsLayout && rightColumnWidth ? { flex: `0 0 ${rightColumnWidth}px`, width: `${rightColumnWidth}px` } : undefined}
        >
          {isTabsLayout ? (
            <Controls isMobile onSuccess={handleOrderSuccess} />
          ) : (
            <div ref={controlsRef} className="futures__controls">
              <Controls onSuccess={handleOrderSuccess} />
            </div>
          )}
        </div>
      </div>

      {shouldShowOnboarding && (
        <FuturesOnboarding
          onClose={completeOnboarding}
          onStartTutorial={() => {
            completeOnboarding();
            setIsGuideOpen(true);
          }}
        />
      )}

      <FuturesOnboardingGuide
        isOpen={isGuideOpen}
        steps={futuresOnboardingSteps}
        onClose={() => {
          completeOnboarding();
          setIsGuideOpen(false);
        }}
      />
    </div>
  );
};

export default Futures;
